import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { buildUrl, isDefined } from '@trimble-gcs/common';
import { combineLatest, filter, map, Observable, of, switchMap, take } from 'rxjs';
import { ConnectProject } from 'trimble-connect-workspace-api';
import { SetConnectRegion, SetConnectRegions } from '../app-state/app.actions';
import { Endpoint } from '../app-state/app.models';
import { AppState } from '../app-state/app.state';
import { AuthState } from '../auth/auth.state';
import { ConnectRegion, ConnectRegionDto } from './connect.models';

@Injectable({
  providedIn: 'root',
})
export class ConnectRegionService {
  private readonly store = inject(Store);
  private readonly http = inject(HttpClient);

  getConnectRegionEndpoint(): Observable<Endpoint> {
    return this.store.selectOnce(AppState.connectRegion).pipe(
      switchMap((connectRegion) => {
        if (isDefined(connectRegion)) return of(connectRegion);

        return this.getAndCacheConnectRegion();
      }),
      map((connectRegion) => connectRegion.endpoint),
    );
  }

  getProjects(): Observable<ConnectProject[]> {
    const baseUrl = this.store.selectSnapshot(AppState.endpoints).connect.url;
    // note: connect also takes parameter mimimal=true, but then the results are not sorted.
    const url = buildUrl(baseUrl, `projects?fullyLoaded=false&sort=-lastVisited`);
    return this.http.get<ConnectProject[]>(url);
  }

  getProjectsForRegion(connectRegion: ConnectRegion): Observable<ConnectProject[]> {
    const baseUrl = connectRegion.endpoint.url;
    const url = buildUrl(baseUrl, `projects?fullyLoaded=false&sort=-lastVisited`);
    const token = this.store.selectSnapshot(AuthState.accessToken);

    return this.http.get<ConnectProject[]>(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  getConnectRegions(): Observable<ConnectRegion[]> {
    return this.store.selectOnce(AppState.connectRegions).pipe(
      switchMap((regions) => {
        if (isDefined(regions)) return of(regions);

        return this.getAndCacheConnectRegions();
      }),
    );
  }

  private getAndCacheConnectRegion(): Observable<ConnectRegion> {
    const project$ = this.store.select(AppState.project).pipe(filter(isDefined));

    return combineLatest([project$, this.getConnectRegions()]).pipe(
      map(([project, regions]) => regions.find((region) => region.location === project.location)),
      filter(isDefined),
      take(1),
      switchMap((region) => {
        return this.store.dispatch(new SetConnectRegion(region)).pipe(map(() => region));
      }),
    );
  }

  private getAndCacheConnectRegions(): Observable<ConnectRegion[]> {
    return this.store.select(AppState.settings).pipe(
      switchMap((settings) => {
        const baseUrl = settings.endpoints.connect.url;
        const url = buildUrl(baseUrl, '/regions');
        return this.http.get<ConnectRegionDto[]>(url);
      }),
      map((regions) => {
        return regions.map((region) => {
          const connectRegion: ConnectRegion = {
            ...region,
            endpoint: {
              url: region['tc-api'],
              protected: true,
            },
          };
          return connectRegion;
        });
      }),
      switchMap((regions) => {
        return this.store.dispatch(new SetConnectRegions(regions)).pipe(map(() => regions));
      }),
    );
  }
}
